/**
 * The list-safety card on `/campaigns/:campaignId` — one read, three panels, and
 * a failure branch that is not allowed to look like good news.
 *
 * ## Its own read, and why
 *
 * `fetchCampaignListSafety` is separate from `fetchCampaign` on
 * `CampaignResultsPanel`'s precedent: it answers a different question at
 * different tables — the wave rows, the stored health snapshot and a
 * distinct-message count over `delivery_events` — and the campaign list, the
 * poll and the create screen have no use for any of it.
 *
 * `refreshKey` is how it follows the send. The parent builds it from the status
 * and the recipient counts, so the card re-reads when a batch releases, when a
 * campaign halts, and when delivery events move the figures the gate is reading
 * — and not on every poll tick that changed nothing.
 *
 * ## A failed read is never rendered as a clean list
 *
 * This is the zero-versus-absent rule applied to a whole card. A request that
 * did not come back is not evidence that a campaign is fine, is not a plan with
 * no batches, and is certainly not a bounce rate of zero — so the error branch
 * is `ErrorState` with the request id and a retry, and nothing else on the card
 * renders beside it. The same argument `NewCampaignPage` had to be corrected on:
 * a load failure reported as a fact about the workspace.
 *
 * That is also what makes this card safe to ship before the API route exists. It
 * shows the failure honestly rather than a screenful of confident zeros.
 *
 * ## The order of the panels is the order of the reader's questions
 *
 * A halted campaign is the only thing on this screen anybody has to *do*
 * something about, so it is first and it carries the action. The batches are
 * next, because "why has it stopped at 102 of 1,278" is the question a holding
 * campaign provokes. The list check is last: on a draft it is a decision, but by
 * the time a campaign is running it is the record of what the list looked like
 * when the plan was built — which is exactly the thing anybody asks for after a
 * bad send, and exactly the thing nobody needs before one.
 */
import { isCampaignTerminal, type Campaign, type CampaignListSafety } from '@wend/shared';
import { Card, CardContent, CardHeader, CardTitle, Spinner } from '@wend/ui';

import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { fetchCampaignListSafety } from '../../lib/api/campaigns';
import { errorMessage } from '../../lib/api/errors';
import { useAsyncData } from '../../lib/use-async-data';
import { CampaignHaltPanel } from './CampaignHaltPanel';
import { CampaignWavesPanel } from './CampaignWavesPanel';
import { ListHealthPanel } from './ListHealthPanel';

export interface CampaignListSafetyCardProps {
  campaign: Campaign;
  /** Changes whenever the send moves, so the card re-reads with it. */
  refreshKey: string;
  /** The campaign as a resume left it. */
  onResumed: (campaign: Campaign) => void;
}

export function CampaignListSafetyCard({
  campaign,
  refreshKey,
  onResumed,
}: CampaignListSafetyCardProps) {
  const { data, error, loadingFirst, reload } = useAsyncData(
    (signal) => fetchCampaignListSafety(campaign.id, signal),
    [campaign.id, refreshKey],
  );

  return (
    <Card>
      <CardHeader>
        {/* Tense, because this card outlives the send. "How this is being sent"
            over a campaign that finished last Tuesday is the small kind of wrong
            that makes a reader distrust the numbers under it. */}
        <CardTitle>
          {isCampaignTerminal(campaign.status) ? 'How this was sent' : 'How this is being sent'}
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        {/* A refresh that failed over data already on screen. Without this the
            wave list keeps describing a batch that has since moved on, and looks
            exactly as authoritative as it did a minute ago — which is the same
            thing the page-level banner above exists to prevent. */}
        {error !== null && data !== null && (
          <FormAlert tone="error">
            {errorMessage(error)} What is shown below is the last version that loaded.
          </FormAlert>
        )}

        {data === null ? (
          loadingFirst ? (
            <div className="flex items-center gap-3 py-6 text-[0.92rem] text-muted">
              <Spinner size="md" label={null} />
              <span role="status">Checking this list…</span>
            </div>
          ) : (
            <ErrorState
              error={error}
              title="Could not check this list"
              onRetry={reload}
              className="flex flex-col items-start gap-3 py-6"
            />
          )
        ) : (
          <Body campaign={campaign} safety={data} onResumed={onResumed} />
        )}
      </CardContent>
    </Card>
  );
}

function Body({
  campaign,
  safety,
  onResumed,
}: {
  campaign: Campaign;
  safety: CampaignListSafety;
  onResumed: (campaign: Campaign) => void;
}) {
  return (
    <div className="flex flex-col gap-7">
      {safety.halt !== null && (
        <CampaignHaltPanel campaign={campaign} halt={safety.halt} onResumed={onResumed} />
      )}

      <CampaignWavesPanel
        campaign={campaign}
        waves={safety.waves}
        plan={safety.plan}
        reading={safety.reading}
        willAttempt={safety.willAttempt}
        reachable={safety.health.signals.total}
        warmUpEnforced={safety.warmUpEnforced}
      />

      <div className="border-t border-line pt-6">
        <ListHealthPanel health={safety.health} />
      </div>
    </div>
  );
}
